import React, { MouseEventHandler, useMemo } from "react";
import graphql from "babel-plugin-relay/macro";
import { MultiSelect } from "../../../components/lib/MultiSelect";
import { Region } from "../types";
import { RegionsField_viewer$key } from "./__generated__/RegionsField_viewer.graphql";
import Box from "../../../components/lib/Box";
import { Selected, Selections } from "./styled";
import { FormControl, Label, LabelWrapper } from "../../../components/lib/Label";
import { Placeholder } from "../../../components/lib/Placeholder";
import { useFragment } from "react-relay";
import Icon from "../../../components/lib/Icon";
import { isNonNullable } from "../../../utils/hashmap";
import { useLazyRelayQuery } from "../../../hooks/useRelayQuery";
import { RegionsFieldCountryQuery } from "./__generated__/RegionsFieldCountryQuery.graphql";
import { onRegionCountryChange } from "../observers";
import { useRateSearchFormStore } from "../../../stores/RateSearchForm";

const countryQuery = graphql`
  query RegionsFieldCountryQuery($searchParam: String!) {
    viewer {
      locations(search: $searchParam) {
        fullTitle @required(action: NONE)
        fullSubtitle
        countryId
        locationId @required(action: NONE)
      }
    }
  }
`;

const fragment = graphql`
  fragment RegionsField_viewer on Viewer {
    regions(order: [{ field: NAME }]) {
      edges {
        node {
          country {
            locationId
            name
            id
          }
          name
          regionId @required(action: NONE)
          locations {
            locationId @required(action: NONE)
            name
            parent {
              locationId @required(action: NONE)
              name
            }
          }
        }
      }
    }
  }
`;

type Props = {
  viewer: RegionsField_viewer$key;
  regions: Region[];
  disabledRegions?: Region[];
  setRegions(regs: Region[]): void;
  hasLocations: boolean;
};

export function RegionsField(props: Props) {
  const { viewer, regions, setRegions, hasLocations, disabledRegions = [] } = props;
  const data = useFragment(fragment, viewer);

  const { send } = useLazyRelayQuery<RegionsFieldCountryQuery>({
    gql: countryQuery,
    onComplete(response) {
      const locations = response?.viewer?.locations?.filter(isNonNullable) || [];
      if (locations.length > 0) {
        const currentIndustries = useRateSearchFormStore.getState().industries;
        const countryId = locations[0].countryId || 0;
        const industryId =
          currentIndustries.length > 0 ? currentIndustries[0].legacyId! : 0;
        if (countryId && industryId) {
          onRegionCountryChange.emit({ countryId, industryId });
        }
      }
    },
  });

  const options = useMemo(
    () => data?.regions?.edges.map((edge) => edge?.node).filter(isNonNullable) || [],
    [data]
  );

  function clear() {
    setRegions([]);
    onRegionCountryChange.emit({ countryId: 0, industryId: 0 });
  }

  function onSelect(region: Region) {
    if (region && !regions.some((r) => r === region)) {
      setRegions([...regions, region]);
      if (region.country?.name) {
        send({ searchParam: region.country.name });
      }
    }
  }

  function unselect(region: Region): MouseEventHandler<HTMLButtonElement> {
    return (e) => {
      e.stopPropagation();
      const newRegions = regions.filter((r) => r.regionId !== region.regionId);
      setRegions(newRegions);
      if (newRegions.length === 0) {
        onRegionCountryChange.emit({ countryId: 0, industryId: 0 });
      }
    };
  }

  return (
    <Box>
      <MultiSelect
        disabled={hasLocations}
        icon="globe"
        selected={regions}
        disabledOptions={disabledRegions}
        label="Region (Optional)"
        id="regionSelect"
        placeholder="Select region"
        readOnly
        options={options}
        renderOption={(o) => o?.name}
        getKey={(o) => String(o?.regionId)}
        onClearSelections={clear}
        noResultsRender="No options."
        onSelect={onSelect}
        renderAddon={(opts) => (
          <Selections>
            {opts.map((o) => (
              <Selected
                key={o.regionId}
                onClick={unselect(o)}
                title={`Unselect ${o.name}`}
              >
                <span>{o.name}</span>
                <Icon size="sm" icon="close" />
              </Selected>
            ))}
          </Selections>
        )}
      />
    </Box>
  );
}

export function RegionsFieldLoader() {
  return (
    <Box>
      <FormControl fill>
        <LabelWrapper>
          <Label>Region</Label>
        </LabelWrapper>
        <Placeholder height={44} />
      </FormControl>
    </Box>
  );
}
